import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Button,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  TextField,
} from '@mui/material';
import DataTable from 'react-data-table-component';
import {
  Clock,
  Lock,
  RefreshCw,
  Play,
  Pencil,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  CircleDashed,
} from 'lucide-react';
import { getApiBaseUrl } from '../utils/baseUrl';
import { useLoading, acquireTokenSafe } from '../App';
import { CRON_PRESETS, describeCron, isValidCron, nextRuns } from '../utils/cron';

const apiUrl = getApiBaseUrl();

interface ScheduledJob {
  Id: number;
  JobName: string;
  Description: string;
  Schedule: string;
  Enabled: boolean;
  LastRunAt: string | null;
  LastStatus: string | null;
  LastError: string | null;
  CreatedAt: string;
  UpdatedAt: string;
}

interface EditDraft {
  JobName: string;
  Description: string;
  Schedule: string;
}

const ScheduledJobs = () => {
  const { instance } = useMsal();
  const { setLoading } = useLoading();
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [runningJobs, setRunningJobs] = useState<Set<number>>(new Set());
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [selectedError, setSelectedError] = useState<string | null>(null);
  const [editJob, setEditJob] = useState<ScheduledJob | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft>({ JobName: '', Description: '', Schedule: '' });
  const [editSaving, setEditSaving] = useState(false);

  const getAuthHeader = async () => {
    const accounts = instance.getAllAccounts();
    const token = await acquireTokenSafe(instance, accounts);
    if (!token) throw new Error('No account found');
    return `Bearer ${token}`;
  };

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/scheduled-jobs`, {
        headers: { Authorization: authHeader },
      });

      if (response.status === 403) {
        setHasAccess(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setHasAccess(true);
      const data = await response.json();
      setJobs(data);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      showSnack('Failed to fetch scheduled jobs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleJob = async (job: ScheduledJob) => {
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/scheduled-jobs/${job.Id}`, {
        method: 'PUT',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !job.Enabled }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setJobs(prev => prev.map(j => (j.Id === job.Id ? { ...j, Enabled: !job.Enabled } : j)));
      showSnack(`Job ${!job.Enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      console.error('Error toggling job:', err);
      showSnack('Failed to update job', 'error');
    }
  };

  const runJob = async (job: ScheduledJob) => {
    setRunningJobs(prev => new Set(prev).add(job.Id));

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/scheduled-jobs/${job.Id}/run`, {
        method: 'POST',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      showSnack(data.message || `${job.JobName} executed successfully`);
      await fetchJobs();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to run ${job.JobName}`;
      showSnack(errorMessage, 'error');
      await fetchJobs();
    } finally {
      setRunningJobs(prev => {
        const next = new Set(prev);
        next.delete(job.Id);
        return next;
      });
    }
  };

  const openEdit = (job: ScheduledJob) => {
    setEditJob(job);
    setEditDraft({
      JobName: job.JobName ?? '',
      Description: job.Description ?? '',
      Schedule: job.Schedule ?? '',
    });
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditJob(null);
  };

  const saveEdit = async () => {
    if (!editJob) return;
    if (!editDraft.JobName.trim()) {
      showSnack('Job name is required', 'error');
      return;
    }
    if (!editDraft.Schedule.trim()) {
      showSnack('Schedule is required', 'error');
      return;
    }
    if (!isValidCron(editDraft.Schedule)) {
      showSnack('Schedule is not a valid 5-field cron expression', 'error');
      return;
    }
    setEditSaving(true);
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/scheduled-jobs/${editJob.Id}`, {
        method: 'PUT',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jobName: editDraft.JobName,
          description: editDraft.Description,
          schedule: editDraft.Schedule,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      setJobs(prev =>
        prev.map(j =>
          j.Id === editJob.Id
            ? {
                ...j,
                JobName: editDraft.JobName.trim(),
                Description: editDraft.Description,
                Schedule: editDraft.Schedule.trim(),
              }
            : j
        )
      );
      showSnack('Job updated successfully');
      setEditJob(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update job';
      showSnack(message, 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const getStatusIcon = (status: string | null, isRunning: boolean) => {
    if (isRunning) return <CircularProgress size={18} />;
    switch (status) {
      case 'Success':
        return <CheckCircle2 size={18} color="#3fb950" />;
      case 'Failed':
        return <XCircle size={18} color="#f85149" />;
      default:
        return <CircleDashed size={18} color="#8b949e" />;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const columns = [
    {
      name: 'Enabled',
      width: '90px',
      cell: (row: ScheduledJob) => (
        <Switch checked={row.Enabled} onChange={() => toggleJob(row)} size="small" color="primary" />
      ),
    },
    {
      name: 'Status',
      width: '80px',
      cell: (row: ScheduledJob) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getStatusIcon(row.LastStatus, runningJobs.has(row.Id))}
        </Box>
      ),
    },
    {
      name: 'Job Name',
      selector: (row: ScheduledJob) => row.JobName,
      sortable: true,
      width: '220px',
    },
    {
      name: 'Description',
      selector: (row: ScheduledJob) => row.Description,
      sortable: false,
      wrap: true,
    },
    {
      name: 'Schedule',
      selector: (row: ScheduledJob) => row.Schedule,
      sortable: false,
      width: '200px',
      cell: (row: ScheduledJob) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Clock size={14} color="#8b949e" />
          <span style={{ fontFamily: 'monospace' }}>{row.Schedule}</span>
        </Box>
      ),
    },
    {
      name: 'Last Run',
      selector: (row: ScheduledJob) => row.LastRunAt || '',
      sortable: true,
      width: '180px',
      cell: (row: ScheduledJob) => formatDate(row.LastRunAt),
    },
    {
      name: 'Last Status',
      width: '130px',
      cell: (row: ScheduledJob) => {
        const hasError = row.LastStatus === 'Failed' && row.LastError;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span
              style={{
                color:
                  row.LastStatus === 'Success'
                    ? '#3fb950'
                    : row.LastStatus === 'Failed'
                    ? '#f85149'
                    : '#8b949e',
              }}
            >
              {row.LastStatus || 'Pending'}
            </span>
            {hasError && (
              <Tooltip title="View error details">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedError(row.LastError);
                    setErrorDialogOpen(true);
                  }}
                >
                  <AlertTriangle size={14} color="#f85149" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      name: 'Actions',
      width: '180px',
      cell: (row: ScheduledJob) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit job">
            <span>
              <button
                className="btn btn-secondary"
                onClick={() => openEdit(row)}
                style={{ padding: '4px 8px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Pencil size={12} />
                Edit
              </button>
            </span>
          </Tooltip>
          <Tooltip title="Run job now">
            <span>
              <button
                className="btn btn-primary"
                onClick={() => runJob(row)}
                disabled={runningJobs.has(row.Id)}
                style={{ padding: '4px 8px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                {runningJobs.has(row.Id) ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <Play size={12} />
                )}
                {runningJobs.has(row.Id) ? 'Running' : 'Run'}
              </button>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: '#161b22' } },
    headRow: { style: { backgroundColor: '#21262d', borderBottom: '1px solid #30363d' } },
    headCells: {
      style: {
        color: '#8b949e',
        fontWeight: 600,
        fontSize: '12px',
        textTransform: 'uppercase' as const,
      },
    },
    rows: {
      style: {
        backgroundColor: '#0d1117',
        borderBottom: '1px solid #21262d',
        color: '#c9d1d9',
        '&:hover': { backgroundColor: '#161b22' },
      },
    },
    cells: { style: { padding: '12px 16px' } },
    pagination: {
      style: { backgroundColor: '#161b22', color: '#c9d1d9', borderTop: '1px solid #30363d' },
    },
  };

  useEffect(() => {
    fetchJobs();
  }, [instance]);

  if (hasAccess === false) {
    return (
      <div className="page-container">
        <div className="access-denied">
          <Lock size={48} />
          <h2>Access Restricted</h2>
          <p>You don't have permission to view Scheduled Jobs.</p>
          <p>Please contact your administrator if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  const enabledCount = jobs.filter(j => j.Enabled).length;
  const failedCount = jobs.filter(j => j.LastStatus === 'Failed').length;
  const successCount = jobs.filter(j => j.LastStatus === 'Success').length;

  return (
    <div className="page-container">
      <div className="page-header compact">
        <div className="page-title">
          <Clock size={20} />
          <h1>Scheduled Jobs</h1>
          <span className="restricted-badge">
            <Lock size={12} />
            Restricted
          </span>
        </div>
        <button
          className="btn btn-secondary"
          onClick={fetchJobs}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      <div className="stats-grid stats-grid-5">
        <div className="stat-card">
          <div className="stat-icon">
            <Clock size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{jobs.length}</div>
            <div className="stat-label">Total Jobs</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#3b82f6' }}>
            <CheckCircle2 size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{enabledCount}</div>
            <div className="stat-label">Enabled</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#3fb950' }}>
            <CheckCircle2 size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{successCount}</div>
            <div className="stat-label">Last Run OK</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#ef4444' }}>
            <XCircle size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{failedCount}</div>
            <div className="stat-label">Last Run Failed</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#8b949e' }}>
            <CircleDashed size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{jobs.length - successCount - failedCount}</div>
            <div className="stat-label">Pending</div>
          </div>
        </div>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={jobs}
          customStyles={customStyles}
          noDataComponent={
            <Box sx={{ p: 4, color: '#8b949e', textAlign: 'center' }}>No scheduled jobs found</Box>
          }
        />
      </div>

      {/* Error Details Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#161b22', color: '#c9d1d9', border: '1px solid #30363d' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #30363d' }}>Error Details</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              backgroundColor: '#0d1117',
              p: 2,
              borderRadius: 1,
              border: '1px solid #30363d',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: '#f85149',
            }}
          >
            {selectedError}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #30363d', p: 2 }}>
          <Button onClick={() => setErrorDialogOpen(false)} sx={{ color: '#c9d1d9' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog
        open={editJob !== null}
        onClose={closeEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#161b22', color: '#c9d1d9', border: '1px solid #30363d' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #30363d', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Pencil size={18} />
          Edit Scheduled Job
        </DialogTitle>
        <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Job Name"
            value={editDraft.JobName}
            onChange={e => setEditDraft(d => ({ ...d, JobName: e.target.value }))}
            fullWidth
            margin="dense"
            variant="outlined"
            InputLabelProps={{ sx: { color: '#8b949e' } }}
            InputProps={{ sx: { color: '#c9d1d9' } }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#8b949e' },
            }}
          />
          <TextField
            label="Description"
            value={editDraft.Description}
            onChange={e => setEditDraft(d => ({ ...d, Description: e.target.value }))}
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            minRows={2}
            InputLabelProps={{ sx: { color: '#8b949e' } }}
            InputProps={{ sx: { color: '#c9d1d9' } }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#8b949e' },
            }}
          />
          <TextField
            label="Schedule (cron expression)"
            value={editDraft.Schedule}
            onChange={e => setEditDraft(d => ({ ...d, Schedule: e.target.value }))}
            fullWidth
            margin="dense"
            variant="outlined"
            helperText="Format: minute hour day-of-month month day-of-week (UTC). e.g. 0 9 * * 1-5"
            InputLabelProps={{ sx: { color: '#8b949e' } }}
            InputProps={{ sx: { color: '#c9d1d9', fontFamily: 'monospace' } }}
            FormHelperTextProps={{ sx: { color: '#8b949e' } }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#8b949e' },
            }}
          />
          <Box>
            <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Presets
            </div>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {CRON_PRESETS.map(preset => {
                const active = editDraft.Schedule.trim() === preset.value;
                return (
                  <Tooltip key={preset.value} title={preset.description}>
                    <button
                      type="button"
                      onClick={() => setEditDraft(d => ({ ...d, Schedule: preset.value }))}
                      style={{
                        background: active ? '#1f6feb' : '#21262d',
                        color: active ? '#fff' : '#c9d1d9',
                        border: `1px solid ${active ? '#1f6feb' : '#30363d'}`,
                        borderRadius: 999,
                        padding: '4px 10px',
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      {preset.label}
                    </button>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
          {(() => {
            const expr = editDraft.Schedule.trim();
            if (!expr) return null;
            const valid = isValidCron(expr);
            const summary = describeCron(expr);
            let upcoming: Date[] = [];
            if (valid) {
              try { upcoming = nextRuns(expr, 3); } catch { /* ignore */ }
            }
            return (
              <Box
                sx={{
                  border: '1px solid #30363d',
                  borderRadius: 1,
                  p: 1.5,
                  backgroundColor: '#0d1117',
                }}
              >
                <div style={{ fontSize: 12, color: '#8b949e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                  Schedule preview
                </div>
                <div style={{ color: valid ? '#c9d1d9' : '#f85149', fontSize: 13, marginBottom: valid ? 8 : 0 }}>
                  {valid ? summary : summary /* describeCron returns 'Invalid: …' */}
                </div>
                {valid && upcoming.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 2 }}>Next runs (UTC):</div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: '#c9d1d9', fontSize: 12, fontFamily: 'monospace' }}>
                      {upcoming.map((d, i) => (
                        <li key={i}>{d.toISOString().replace('T', ' ').slice(0, 16)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #30363d', p: 2 }}>
          <Button onClick={closeEdit} disabled={editSaving} sx={{ color: '#c9d1d9' }}>
            Cancel
          </Button>
          <Button
            onClick={saveEdit}
            disabled={editSaving}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #238636 30%, #2ea043 90%)',
              '&:disabled': { background: '#484f58', color: '#8b949e' },
            }}
          >
            {editSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ScheduledJobs;
